import { Accordion } from "@szhsin/react-accordion";
import { ArrowIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import { Category, CategoryTypes } from "modules/categories";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Icon } from "ui";

import {
  MAX_PRICE_FILTER_SEARCH_PARAMETER,
  MIN_PRICE_FILTER_SEARCH_PARAMETER,
  STATUS_FILTER_SEARCH_PARAMETER
} from "../../../../constants";
import { ProductStatusFilterValues } from "../../../../product.types";
import { CategoryItemAccordion } from "../CategoryItemAccordion";
import { FilterAccordion } from "../FilterAccordion";
import { CategoriesFilterProps } from "./CategoriesFilter.types";

import styles from "./CategoriesFilter.module.scss";

const CategoriesFilter = ({
  categories,
  isCategoriesLoading
}: CategoriesFilterProps) => {
  const { t } = useTranslation();
  const [isFullListShown, setIsFullListShown] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get(CategoryTypes.MAIN_CATEGORY);
  const selectedSubCategory = searchParams.get(CategoryTypes.SUB_CATEGORY);
  const selectedSubChildCategory = searchParams.get(
    CategoryTypes.SUB_CHILD_CATEGORY
  );

  const handleCategorySelect = (category: Category) => {
    const statusFilterValue = searchParams.get(STATUS_FILTER_SEARCH_PARAMETER);

    switch (category.type) {
      case CategoryTypes.MAIN_CATEGORY:
        setSearchParams(
          createSearchParams({
            [STATUS_FILTER_SEARCH_PARAMETER]:
              statusFilterValue ?? ProductStatusFilterValues.ALL,
            [category.type]: category.id
          })
        );
        break;

      case CategoryTypes.SUB_CATEGORY:
        setSearchParams(
          createSearchParams({
            [STATUS_FILTER_SEARCH_PARAMETER]:
              statusFilterValue ?? ProductStatusFilterValues.ALL,
            [CategoryTypes.MAIN_CATEGORY]: String(category.parentCategoryId),
            [category.type]: category.id
          })
        );
        break;

      case CategoryTypes.SUB_CHILD_CATEGORY:
        setSearchParams(
          createSearchParams({
            [STATUS_FILTER_SEARCH_PARAMETER]:
              statusFilterValue ?? ProductStatusFilterValues.ALL,
            [CategoryTypes.MAIN_CATEGORY]: String(
              categories.find((mainCategory) =>
                mainCategory.childCategories?.some(
                  (subCategory) => subCategory.id === category.parentCategoryId
                )
              )?.id
            ),
            [CategoryTypes.SUB_CATEGORY]: String(category.parentCategoryId),
            [category.type]: category.id
          })
        );
        break;
      default:
        break;
    }
  };

  const clearCategoriesFilters = () => {
    searchParams.delete(CategoryTypes.MAIN_CATEGORY);
    searchParams.delete(CategoryTypes.SUB_CATEGORY);
    searchParams.delete(CategoryTypes.SUB_CHILD_CATEGORY);
    searchParams.delete(MIN_PRICE_FILTER_SEARCH_PARAMETER);
    searchParams.delete(MAX_PRICE_FILTER_SEARCH_PARAMETER);

    setSearchParams(searchParams);
  };

  const displayedCategories = useMemo(
    () => (isFullListShown ? categories : categories.slice(0, 5)),
    [categories, isFullListShown]
  );

  return (
    <Accordion transition transitionTimeout={250}>
      <FilterAccordion
        header={t("Category")}
        isClearDisplay={Boolean(
          selectedCategory ?? selectedSubCategory ?? selectedSubChildCategory
        )}
        onClearClick={clearCategoriesFilters}
      >
        <If condition={isCategoriesLoading}>
          <div className={styles.categoriesFilter_loader}>
            <ClipLoader size={24} color="#fb3973" />
          </div>
        </If>

        {displayedCategories.map((category) => {
          return (
            <Accordion key={category.id}>
              <CategoryItemAccordion
                category={category}
                isCategorySelected={
                  searchParams.get(category.type) === category.id
                }
                onSelect={handleCategorySelect}
              />
            </Accordion>
          );
        })}

        <button
          className={cn(styles.categoriesFilter_viewMoreBtn, {
            [styles.categoriesFilter_viewMoreBtn__active]: isFullListShown
          })}
          onClick={() => setIsFullListShown((prev) => !prev)}
        >
          <Icon
            as={ArrowIcon}
            boxSize={20}
            className={styles.categoriesFilter_viewMoreBtn_icon}
          />
          {isFullListShown ? t("Hide") : t("View More")}
        </button>
      </FilterAccordion>
    </Accordion>
  );
};

export default CategoriesFilter;
